import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { ApiContext } from '@api/api';
import { CargoTemplateWithoutIdDTO, CGV2SmallCompanyOutDTO, Pageable, TemplateKindEnum, TmplV2ListFilterInDTO, TmplV2ListItemOutDTO } from '@api/ringil-api.v2';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import { ToastContext } from '@components/auth/ToastContext.tsx';
import RingilDataTable, { ColumnConfig, FilterConfig, FilterType, PagedData } from '@components/RingilDataTable/RingilDataTable.tsx';
import MoreButton from '@components/RingilComponents/MoreButton/MoreButton.tsx';
import ConfirmDialog from '@components/RingilComponents/ConfirmDialog.tsx';
import SvgChevron from '@icons/chevron-thin.svg?react';
import useCustomerCarriers from '@hooks/customer-carrier/customer-carriers.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import useNavigation from '@hooks/useNavigation.tsx';
import { stringDateTimeWithLocaleFromApiStringInTz } from '@utils/dateWithTz.tsx';
import { formatPriceRequest } from '@utils/priceUtils.ts';
import dayjs from 'dayjs';
import { ContractedCarrierForm, fromContractedCarrierOutDTO, fromPriceTableDTO, PriceTableForm } from '@components/ringil3/Features/Contracted/types.ts';
import { getCell, levelForDate } from '@utils/contractedPriceTable.ts';

/** URL/filter-bar shape; ladenFactor arrives as text from the input. */
interface LinesFilter extends Omit<TmplV2ListFilterInDTO, 'ladenFactor' | 'templateKind'> {
  ladenFactor?: string | number;
}

type TableRef = { reloadData: () => void; selectedObjects: unknown[]; clear: () => void };

/** "Linky" tab of the templates page — Template 2.0 of kind LINE, with their contracted carriers expandable per row. */
const LinesList: React.FC = () => {
  const { tr } = useTranslationLgs();
  const { listTmplV2, deleteTmplV2, getCGLocationsV2, lovCargoItemTypeForCompany } = useContext(ApiContext);
  const { loggedUser } = useContext(AuthenticatedUserContext);
  const { showToastMessage } = useContext(ToastContext);
  const nav = useNavigation();
  const { carriers } = useCustomerCarriers();
  const tableRef = useRef<TableRef>(null);

  const [locations, setLocations] = useState<CGV2SmallCompanyOutDTO[]>([]);
  const [cargoTemplates, setCargoTemplates] = useState<CargoTemplateWithoutIdDTO[]>([]);
  const [deleteLine, setDeleteLine] = useState<TmplV2ListItemOutDTO>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    getCGLocationsV2({ query: '' }, data => setLocations(data ?? []));
    if (loggedUser?.companyId) lovCargoItemTypeForCompany(loggedUser.companyId, data => setCargoTemplates(data ?? []));
  }, [loggedUser?.companyId]);

  const plural = (key: string, count: number, one: string, few: string, other: string) =>
    tr(key, other, { count, defaultValue_one: one, defaultValue_few: few, defaultValue_many: other, defaultValue_other: other });

  const defaultLf = tr('LinesList.defaultLadenFactor', 'Výchozí');

  const loadData = (filter: LinesFilter, pageable: Pageable, onSuccess: (data: PagedData<TmplV2ListItemOutDTO>) => void) => {
    listTmplV2(
      {
        ...filter,
        templateKind: TemplateKindEnum.LINE,
        ladenFactor: filter.ladenFactor !== undefined && filter.ladenFactor !== '' ? Number(filter.ladenFactor) : undefined,
      },
      pageable,
      data => onSuccess(data),
    );
  };

  const confirmDelete = () => {
    if (!deleteLine) return;
    setDeleting(true);
    deleteTmplV2(
      deleteLine.id,
      () => {
        setDeleting(false);
        setDeleteLine(null);
        showToastMessage(tr('LinesList.deletedTitle', 'Hotovo'), tr('LinesList.deleted', 'Linka byla smazána'), 'success');
        tableRef.current?.reloadData();
      },
      null,
      {
        onError: (_e, showStandardError) => {
          setDeleting(false);
          showStandardError();
        },
      },
    );
  };

  const stopLabel = (label: string | undefined, extra: number) => {
    const name = label ?? tr('LinesList.anyLocation', 'Libovolná lokalita');
    return extra > 0 ? `${name} +${extra}` : name;
  };

  const locationOptions = useMemo(
    () =>
      locations.flatMap(company =>
        (company.locations ?? []).map(l => ({ label: `${l.locationName} (${company.companyName})`, value: l.locationId, active: true })),
      ),
    [locations],
  );

  const filtersConf: FilterConfig[] = [
    {
      filterType: FilterType.InputText,
      label: tr('LinesList.filterName', 'Název linky'),
      placeholder: tr('LinesList.filterName', 'Název linky'),
      value: ['name'],
    },
    {
      filterType: FilterType.Dropdown,
      label: tr('LinesList.filterTargetLocation', 'Cílová lokalita'),
      placeholder: tr('LinesList.filterTargetLocation', 'Cílová lokalita'),
      value: ['targetLocationId'],
      options: locationOptions,
    },
    {
      filterType: FilterType.MultiSelect,
      label: tr('LinesList.filterCarrier', 'Dopravce'),
      placeholder: tr('LinesList.filterCarrier', 'Dopravce'),
      value: ['carrierIds'],
      options: carriers.map(c => ({ label: c.name, value: c.id, active: true })),
    },
    {
      filterType: FilterType.MultiSelect,
      label: tr('LinesList.filterMu', 'M. jednotka'),
      placeholder: tr('LinesList.filterMu', 'M. jednotka'),
      value: ['cargoTemplateCodes'],
      options: cargoTemplates.map(c => ({ label: c.name, value: c.code, active: true })),
    },
    {
      filterType: FilterType.InputText,
      label: tr('LinesList.filterLadenFactor', 'Ladenfactor'),
      placeholder: tr('LinesList.filterLadenFactor', 'Ladenfactor'),
      value: ['ladenFactor'],
    },
  ];

  const columns: ColumnConfig<TmplV2ListItemOutDTO>[] = [
    {
      id: 'name',
      field: 'name',
      header: 'LinesList.colLine',
      headerDefault: 'Linka',
      width: '30%',
      sortable: true,
      order: 1,
      render: row => (
        <div className={'flex flex-col gap-1'}>
          <Link to={nav.createNavigationLink(nav.urlFunctions.createCustomerLineDetail(row.id))} className={'text-content-brand font-medium'}>
            {row.name}
          </Link>
          <div className={'text-xs text-content-secondary'}>
            {stopLabel(row.firstStopLabel, row.firstStopExtraCount)} › {stopLabel(row.lastStopLabel, row.lastStopExtraCount)}
          </div>
        </div>
      ),
    },
    {
      id: 'carriers',
      header: 'LinesList.colCarrier',
      headerDefault: 'Dopravce',
      width: '20%',
      order: 2,
      render: (row, toggle, expanded) =>
        row.carrierCount > 0 ? (
          <button
            type={'button'}
            className={'flex items-center gap-2 text-content-brand bg-transparent border-0 p-0 cursor-pointer'}
            onClick={() => toggle?.(row)}
            data-testid={`lines-toggle-${row.id}`}
          >
            {plural('LinesList.carrierCount', row.carrierCount, '{{count}} dopravce', '{{count}} dopravci', '{{count}} dopravců')}
            <SvgChevron className={clsx('w-3 h-3 transition-transform', expanded && 'rotate-180')} />
          </button>
        ) : (
          <span className={'text-content-secondary'}>—</span>
        ),
    },
    {
      id: 'mu',
      header: 'LinesList.colMu',
      headerDefault: 'M. jednotka',
      width: '12%',
      order: 3,
      // per row of the expansion
      render: () => <span className={'text-content-secondary'}>—</span>,
    },
    {
      id: 'ladenFactor',
      header: 'LinesList.colLadenFactor',
      headerDefault: 'Ladenfaktor',
      width: '10%',
      order: 4,
      render: () => <span className={'text-content-secondary'}>—</span>,
    },
    {
      id: 'price',
      header: 'LinesList.colPrice',
      headerDefault: 'Cena / Jedn.',
      width: '12%',
      order: 5,
      render: row => <span>{plural('LinesList.priceCount', row.priceCount, '{{count}} cena', '{{count}} ceny', '{{count}} cen')}</span>,
    },
    {
      id: 'updatedAt',
      field: 'updatedAt',
      header: 'LinesList.colUpdated',
      headerDefault: 'Upraveno',
      width: '12%',
      sortable: true,
      isDefaultSort: true,
      defaultSortDirection: 'DESC',
      order: 6,
      render: row => (
        <div className={'flex flex-col'}>
          <span>{row.updatedAt ? stringDateTimeWithLocaleFromApiStringInTz(row.updatedAt, loggedUser?.defaultTimezoneId) : '—'}</span>
          {row.updatedByName && <span className={'text-xs text-content-secondary'}>{row.updatedByName}</span>}
        </div>
      ),
    },
    {
      id: 'actions',
      width: '4%',
      order: 7,
      render: row => (
        <MoreButton
          buttonMode={'table'}
          sections={[
            {
              items: [
                {
                  id: 'edit',
                  label: tr('LinesList.edit', 'Upravit'),
                  command: () => nav.navigate(nav.urlFunctions.createCustomerLineDetail(row.id)),
                },
                {
                  id: 'delete',
                  label: tr('LinesList.delete', 'Smazat'),
                  command: () => setDeleteLine(row),
                },
              ],
            },
          ]}
        />
      ),
    },
  ];

  // fromPriceTableDTO gives every row a fresh client key: mapped once per loaded row, so re-renders keep the React keys
  const pricedRows = useRef(new WeakMap<TmplV2ListItemOutDTO, { table: PriceTableForm; forms: ContractedCarrierForm[] } | null>());
  const pricedRow = (row: TmplV2ListItemOutDTO) => {
    if (!pricedRows.current.has(row)) {
      const table = row.priceTable ? fromPriceTableDTO(row.priceTable) : null;
      pricedRows.current.set(row, table ? { table, forms: (row.carriers ?? []).map(carrier => fromContractedCarrierOutDTO(carrier, table)) } : null);
    }
    return pricedRows.current.get(row);
  };

  /**
   * One row per carrier × price row (MU + laden factor or Výchozí), aligned with the parent columns. Prices are those
   * of the level valid today, else of Úroveň 0.
   */
  const expansion = (row: TmplV2ListItemOutDTO) => {
    const priced = pricedRow(row);
    if (!priced) return null;
    const { table, forms } = priced;
    const today = dayjs().format('YYYY-MM-DD');
    const level = levelForDate(table, today) ?? table.levels.find(l => l.key === table.defaultLevelKey) ?? table.levels[0];
    return (
      <div className={'flex flex-col'} data-testid={`lines-expansion-${row.id}`}>
        {level && (
          <div className={'grid grid-cols-[30%_20%_12%_10%_12%_12%_4%] py-1 text-xs text-content-secondary'}>
            <div />
            <div className={'col-span-5'}>{tr('LinesList.pricesOfLevel', 'Ceny úrovně {{level}}', { level: level.name })}</div>
          </div>
        )}
        {(row.carriers ?? []).flatMap((carrier, index) => {
          const form = forms[index];
          return table.rows.map(priceRow => {
            const price = level ? getCell(form.prices, level.key, priceRow.key) : null;
            return (
              <div
                key={`${carrier.carrierId}-${priceRow.key}`}
                className={'grid grid-cols-[30%_20%_12%_10%_12%_12%_4%] items-center py-2 text-sm border-0 border-b border-solid border-content-quaternary'}
              >
                <div />
                <div className={'flex flex-col'}>
                  <span>{carrier.carrierName}</span>
                  {carrier.users?.length > 0 && <span className={'text-xs text-content-secondary'}>{carrier.users.map(u => u.fullName).join(', ')}</span>}
                </div>
                <div>{priceRow.cargoTemplateName || priceRow.cargoTemplateCode}</div>
                <div>{priceRow.ladenFactor ?? defaultLf}</div>
                <div>{price !== null ? formatPriceRequest({ price, currency: table.currency }) : '—'}</div>
                <div />
                <div>
                  <MoreButton
                    buttonMode={'table'}
                    sections={[
                      {
                        items: [
                          {
                            id: 'edit',
                            label: tr('LinesList.edit', 'Upravit'),
                            command: () => nav.navigate(nav.urlFunctions.createCustomerLineDetail(row.id)),
                          },
                        ],
                      },
                    ]}
                  />
                </div>
              </div>
            );
          });
        })}
      </div>
    );
  };

  return (
    <>
      <RingilDataTable
        ref={tableRef}
        columnConfig={columns as ColumnConfig<unknown>[]}
        filtersConf={filtersConf}
        loadDataFromApi={loadData}
        filterName={'template_v2_lines_list'}
        rowExpansionTemplate={expansion as (row: unknown) => React.ReactNode}
        emptyMessage={() => tr('LinesList.empty', 'Zatím nemáte žádné linky.')}
      />
      <ConfirmDialog
        visible={!!deleteLine}
        dialogTitle={tr('LinesList.deleteTitle', 'Smazat linku')}
        dialogMessage={tr('LinesList.deleteMessage', 'Opravdu chcete smazat linku {{name}}?', { name: deleteLine?.name })}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteLine(null)}
        onAcceptButtonText={tr('LinesList.deleteConfirm', 'Smazat')}
        onCancelButtonText={tr('LinesList.deleteCancel', 'Zrušit')}
        loading={deleting}
      />
    </>
  );
};

export default LinesList;
